import { memo, type ChangeEvent } from 'react';
import { isR2RenderEnabled } from '../../game/render/r2/flag';
import type { GameSettings } from '../../game/runtime/GameRuntime';

function settingNumber(value: string, min: number, max: number): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return min;
  return Math.max(min, Math.min(max, parsed));
}

interface SettingsPanelProps {
  settings: GameSettings;
  onChange: (next: GameSettings) => void;
  onClose: () => void;
}

/** Fix 3(b): onChange/onClose are already stable (App wraps them in useCallback). */
export const SettingsPanel = memo(function SettingsPanel({ settings, onChange, onClose }: SettingsPanelProps) {
  const impactLabel = isR2RenderEnabled() ? 'IMPACT FX' : 'SCREEN SHAKE';
  return (
    <section className="modal-panel settings-panel" role="dialog" aria-modal="true" aria-labelledby="settings-title">
      <p className="eyebrow">RUNTIME CALIBRATION</p>
      <h2 id="settings-title">SETTINGS</h2>
      <div className="settings-grid">
        <RangeSetting label="MASTER VOLUME" value={settings.master} onChange={(value) => onChange({ ...settings, master: value })} />
        <RangeSetting label="MUSIC VOLUME" value={settings.music} onChange={(value) => onChange({ ...settings, music: value })} />
        <RangeSetting label="SFX VOLUME" value={settings.sfx} onChange={(value) => onChange({ ...settings, sfx: value })} />
        <RangeSetting label={impactLabel} value={settings.shake} onChange={(value) => onChange({ ...settings, shake: value })} />
        <RangeSetting label="TOUCH OPACITY" value={settings.touchOpacity} onChange={(value) => onChange({ ...settings, touchOpacity: value })} />
        <ToggleSetting label="FLASH REDUCTION" checked={settings.flashReduction} onChange={(value) => onChange({ ...settings, flashReduction: value })} />
        <ToggleSetting label="GAMEPAD RUMBLE" checked={settings.rumble} onChange={(value) => onChange({ ...settings, rumble: value })} />
      </div>
      <div className="panel-actions">
        <button data-menu-item type="button" className="button primary" onClick={onClose}>APPLY &amp; RETURN</button>
      </div>
    </section>
  );
});

interface RangeSettingProps { label: string; value: number; onChange: (value: number) => void; }
function RangeSetting({ label, value, onChange }: RangeSettingProps) {
  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => onChange(settingNumber(event.currentTarget.value, 0, 1));
  return (
    <label className="range-setting">
      <span>{label}<output>{Math.round(value * 100)}%</output></span>
      <input data-menu-item type="range" min="0" max="1" step="0.05" value={value} onChange={handleChange} />
    </label>
  );
}

interface ToggleSettingProps { label: string; checked: boolean; onChange: (value: boolean) => void; }
function ToggleSetting({ label, checked, onChange }: ToggleSettingProps) {
  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => onChange(event.currentTarget.checked);
  return (
    <label className="toggle-setting">
      <span>{label}</span>
      <input data-menu-item type="checkbox" checked={checked} onChange={handleChange} />
      <i aria-hidden="true" />
    </label>
  );
}
